import { useStore } from '@store/index';
import { Phone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import type { CaseDetailsTabType } from '@/constants/caseDetails';

import { shouldShowCallButton } from '../config/twilio.config';
import { useManualTaskStarted } from '../hooks/useManualTaskStarted';
import { useIsCurrentUserAssigned } from '../hooks/usePermissions';
import { isAnyCallInProgress } from '../services/voice';
import { Button } from './ui/button';
import { SimpleTooltipV2 } from './ui/tooltip';

interface TwilioCallButtonProps {
  phoneNumber: string;
  caseRecordId: string;
  workflowName: string;
  workflowType?: string;
  state: string;
  assignedUser?: string | null; // Deprecated email of assigned user
  assignedUserId?: string | null; // Canonical id of assigned user (preferred)
  callIndex?: number;
  activeTab?: CaseDetailsTabType;
  // Why a new call cannot be placed right now (e.g. the previous attempt has
  // no disposition yet — see utils/callGuard). The server enforces the same
  // rule; this just surfaces it before the rejected request.
  blockedReason?: string | null;
}

export function TwilioCallButton({
  phoneNumber,
  caseRecordId,
  workflowName,
  workflowType,
  state,
  assignedUser,
  assignedUserId,
  callIndex = 0,
  activeTab,
  blockedReason,
}: TwilioCallButtonProps) {
  // Get openDialpad from store
  const { openDialpad, selectedOrganizationId, selectedProgramId } = useStore();
  const location = useLocation();
  const isCurrentUserAssigned = useIsCurrentUserAssigned();
  const { notifyManualTaskStarted } = useManualTaskStarted(caseRecordId);

  // Always use the insurance phone number from the case input field
  const phoneToCall = phoneNumber;

  // Check if a call is already in progress - use state that updates
  const [isCallActive, setIsCallActive] = useState(false);

  // Check call status periodically
  useEffect(() => {
    const checkCallStatus = () => {
      setIsCallActive(isAnyCallInProgress());
    };

    // Check immediately
    checkCallStatus();

    // Check periodically while component is mounted
    const interval = setInterval(checkCallStatus, 500);

    return () => clearInterval(interval);
  }, []);

  // Check if call button should be shown for this state
  if (!shouldShowCallButton(state)) {
    return null;
  }

  // Only show call button if current user is the assigned user (or no assignment)
  if (!isCurrentUserAssigned(assignedUser, assignedUserId)) {
    return null;
  }

  // Handle dialpad button click
  const handleOpenDialpad = () => {
    // Kept as a backstop even though the button is disabled while blocked:
    // isCallActive is polled at 500ms, so a click can still land in the gap.
    if (isCallActive) {
      toast.error('A call is already in progress. Please end the current call first.');
      return;
    }
    if (blockedReason) {
      toast.error(blockedReason);
      return;
    }
    // Fire the manual-task-started marker (hermes PR #820 / BI-913). The button
    // only renders in human-call states for the assigned user, so no state gate.
    notifyManualTaskStarted();
    openDialpad(phoneToCall, {
      caseRecordId,
      workflowName,
      workflowType,
      callIndex,
      returnPath: location.pathname,
      caseDetailsTab: activeTab, // Pass the active tab to preserve it
      // The call is placed in the program the case page is showing. The stack
      // panel keeps using it even if the sidebar changes mid-call.
      organizationId: selectedOrganizationId ?? undefined,
      programId: selectedProgramId ?? undefined,
    });
  };

  // Why the button is disabled, shown on hover. In-progress takes precedence
  // over blockedReason: it is the more immediate reason, and the two can hold
  // at once (a live call has no disposition either).
  const blockMessage = isCallActive
    ? 'A call is already in progress. End the current call first.'
    : blockedReason || null;
  const isBlocked = !!blockMessage;

  return (
    // The reason has to live on a wrapper, not in the button's `title`: the
    // shadcn Button base sets `disabled:pointer-events-none`, so a disabled
    // button never receives hover and neither a `title` nor a tooltip on the
    // button itself ever fires (BV-1937). The wrapper gets the hover the
    // button passes through. `cursor-not-allowed` moves for the same reason.
    <SimpleTooltipV2 content={blockMessage ?? ''} className={isBlocked ? 'cursor-not-allowed' : ''}>
      <Button
        onClick={handleOpenDialpad}
        variant="default"
        size="sm"
        className={`h-auto px-3 py-1 text-xs font-medium inline-flex items-center gap-1.5 rounded ${
          isBlocked ? 'opacity-50' : ''
        }`}
        disabled={isBlocked}
      >
        <Phone className="h-3.5 w-3.5" />
        <span>Call</span>
      </Button>
    </SimpleTooltipV2>
  );
}
